import { forwardRef, useEffect, useMemo } from 'react';
import { type Camera, type Event, type OrthographicCamera, type PerspectiveCamera } from 'three';
import { Vector2 } from 'three';

import { EventManager, ReactThreeFiber, useFrame, useThree } from '@react-three/fiber';
import { CameraControlsImpl, World } from '@spazious/dollhouse-three';
import { isTouchEvent } from '@spazious/dollhouse-utils';

export type CameraControlsChangeEvent = Event & {
  target: EventTarget & { object: Camera };
};

export interface CameraControlsProps {
  enablePan?: boolean;
  view?: 'orbit' | 'zenithal' | 'walk';
  world?: World;
  camera?: Camera;
  domElement?: HTMLElement;
  makeDefault?: boolean;
  onChange?: (e?: Event) => void;
  onEnd?: (e: Event, camera: Camera) => void;
  onStart?: (e?: Event) => void;
  walkTest?: (cursor: Vector2) => boolean;
  regress?: boolean;
  target?: ReactThreeFiber.Vector3;
  keyEvents?: boolean | HTMLElement;
}

export const CameraControls = forwardRef<CameraControlsImpl, CameraControlsProps>(
  ({ makeDefault, camera, regress, domElement, onChange, onStart, onEnd, ...restProps }, ref) => {
    const invalidate = useThree((state) => state.invalidate);
    const defaultCamera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);
    const events = useThree((state) => state.events) as EventManager<HTMLElement>;
    const setEvents = useThree((state) => state.setEvents);
    const set = useThree((state) => state.set);
    const get = useThree((state) => state.get);
    const performance = useThree((state) => state.performance);
    const explCamera = (camera || defaultCamera) as OrthographicCamera | PerspectiveCamera;
    const explDomElement = (domElement || events.connected || gl.domElement) as HTMLElement;

    const controls = useMemo(() => new CameraControlsImpl(explCamera), [explCamera]);

    // TODO implement a better way to get the normalized cursor
    // @ts-ignore
    controls.getNormalizedCursor = (evt: PointerEvent) => {
      const elem = gl.domElement;
      const { clientX, clientY } = isTouchEvent(evt) ? evt.changedTouches[0] : evt;

      return new Vector2().fromArray([
        ((clientX - elem.clientLeft) / elem.clientWidth) * 2.0 - 1.0,
        -((clientY - elem.clientTop) / elem.clientHeight) * 2.0 + 1.0,
      ]);
    };

    useFrame(({ invalidate }, delta) => {
      if (controls.enabled) {
        if (controls.update(delta)) {
          invalidate();
        }
      }
    }, -1);

    useEffect(() => {
      controls.connect(explDomElement);
      return () => void controls.dispose();
    }, [explDomElement, regress, controls, invalidate]);

    useEffect(() => {
      const callback = (e: Event) => {
        invalidate();
        if (regress) performance.regress();
        if (onChange) onChange(e);
      };

      const onStartCb = (e: Event) => {
        if (onStart) onStart(e);
      };

      const onEndCb = (e: Event) => {
        if (onEnd && explCamera) onEnd(e, explCamera);
      };

      controls.addEventListener('control', callback);
      controls.addEventListener('controlstart', onStartCb);
      controls.addEventListener('controlend', onEndCb);
      controls.addEventListener('wake', callback);
      controls.addEventListener('update', callback);
      controls.addEventListener('transitionstart', callback);

      return () => {
        controls.removeEventListener('control', callback);
        controls.removeEventListener('controlstart', onStartCb);
        controls.removeEventListener('controlend', onEndCb);
        controls.removeEventListener('wake', callback);
        controls.removeEventListener('update', callback);
        controls.removeEventListener('transitionstart', callback);
      };
    }, [
      onChange,
      onStart,
      onEnd,
      controls,
      invalidate,
      setEvents,
      regress,
      performance,
      explCamera,
    ]);

    useEffect(() => {
      if (makeDefault) {
        const old = get().controls;
        set({ controls: controls as any });
        return () => set({ controls: old });
      }
    }, [makeDefault, controls, get, set]);

    return <primitive ref={ref} object={controls} {...restProps} />;
  }
);

export default CameraControls;
